import React, { useState, useCallback, useMemo, useContext } from 'react';
import { PrimaryButtonLightBg } from 'apps/sigma2-ui/src/components/PrimaryButtonLightBg';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { useDashboards } from 'apps/sigma2-ui/src/lib/hooks/useDashboards';
import { LightModal } from 'apps/sigma2-ui/src/components/ui/LightModal';
import { OrgSwitcherContext } from 'apps/sigma2-ui/src/components/OrgSwitcherContext';
import { TouchTarget } from 'apps/sigma2-ui/src/components/ui/TouchTarget';
import { IconChevronRightSvg } from 'apps/sigma2-ui/src/components/svg/IconChevronRight';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { CircularProgress } from '@mui/material';
import { Dropdown } from 'apps/sigma2-ui/src/components/Dropdown';
import { IconArrowTopSvg } from 'apps/sigma2-ui/src/components/svg/IconArrowTop';
import { truncateTextIfNecessary } from 'libs/end-to-end/src/success-phenotype-v2-utils';
import { IconCheckMark24pxSvg } from 'apps/sigma2-ui/src/components/svg/IconCheckMark24px';
import { IconArrowLeft24pxSvg } from 'apps/sigma2-ui/src/components/svg/IconArrowLeft24px';
import { IconInformationSvg } from 'apps/sigma2-ui/src/components/svg/IconInformation';
import { IconBuildingSvg } from 'apps/sigma2-ui/src/components/svg/IconBuilding';
import { IconGlobeSvg } from 'apps/sigma2-ui/src/components/svg/IconGlobe';
import { Spinner } from 'apps/sigma2-ui/src/components/ui/Spinner';
import { useObservation } from 'apps/sigma2-ui/src/state/observation';
import { isEntitledToPublishDashboards } from 'libs/end-to-end/src/group-entitlements';
import cn from 'classnames';
import { DraftDashboard } from 'libs/end-to-end/src/dashboard';
import { IconDownloadSvg } from 'apps/sigma2-ui/src/components/svg/IconDownload';
import { IconDocumentFileSvg } from 'apps/sigma2-ui/src/components/svg/IconDocumentFile';

export interface PublishDashboardModalProps {
  dashboardId: string;
  dashboard: DraftDashboard;
  showDownloadDashboardButton?: boolean;
  onDownloadDashboard?: (onComplete?: () => void) => void;
  downloadPending?: boolean;
  renderTrigger: (props: { onClick: () => void }) => React.ReactNode;
}

export const PublishDashboardModal = (props: PublishDashboardModalProps) => {
  const {
    dashboard,
    dashboardId,
    showDownloadDashboardButton = false,
    onDownloadDashboard,
    downloadPending = false,
    renderTrigger,
  } = props;

  const { myObservation } = useObservation();

  const canPublishDashboards = useMemo(() => {
    return isEntitledToPublishDashboards({
      features: myObservation?.features ?? [],
    });
  }, [myObservation?.features]);

  const language = useLanguage();

  const { selectedOrganization } = useContext(OrgSwitcherContext);

  const [showModal, setShowModal] = useState(false);
  const [showEmbedModal, setShowEmbedModal] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleToggleShowModal = useCallback(() => {
    setShowModal(!showModal);
    // Reset embed modal state when closing the main modal
    if (showModal) {
      setError(null);
      setShowEmbedModal(false);
      setShowDownloadModal(false);
    }
  }, [showModal, setError]);

  const [isPublic, setIsPublic] = useState(dashboard.isPublic);

  const handleMakePublic = useCallback(() => {
    setIsPublic(true);
  }, []);

  const handleMakePrivate = useCallback(() => {
    setIsPublic(false);
  }, []);

  const { updateDashboard } = useDashboards();

  const handlePublish = useCallback(async () => {
    setIsSaving(true);
    setError(null);

    const result = await updateDashboard(dashboardId, {
      ...dashboard,
      isPublic,
    });

    setIsSaving(false);

    if (!result) {
      setError(language.unknownError());
    } else {
      handleToggleShowModal();
    }
  }, [
    updateDashboard,
    dashboardId,
    dashboard,
    isPublic,
    language,
    handleToggleShowModal,
  ]);

  const handleToggleEmbedModal = useCallback(() => {
    setShowEmbedModal(!showEmbedModal);
  }, [showEmbedModal]);

  const handleToggleDownloadModal = useCallback(() => {
    setShowDownloadModal(!showDownloadModal);
  }, [showDownloadModal]);

  const handleDownloadDashboard = useCallback(() => {
    if (!onDownloadDashboard || downloadPending) {
      return;
    }

    onDownloadDashboard(() => {
      setShowDownloadModal(false);
      setShowModal(false);
      setError(null);
    });
  }, [downloadPending, onDownloadDashboard]);

  const iframeCode = useMemo(() => {
    return `<iframe src="${window.location.origin}/embed/dashboards/${dashboardId}" width="100%" height="100%"/>`;
  }, [dashboardId]);

  const handleCopyEmbedCode = useCallback(() => {
    navigator.clipboard.writeText(iframeCode);
  }, [iframeCode]);

  const [generalAccessDropdownOpen, setGeneralAccessDropdownOpen] =
    useState(false);

  const truncatedOrganizationName = useMemo(() => {
    return truncateTextIfNecessary({
      text: selectedOrganization?.name ?? '',
      maxChars: 40,
    });
  }, [selectedOrganization?.name]);

  const generalAccessTrigger = useMemo(() => {
    return (
      <div
        className={cn('flex gap-1 items-center', {
          '-mx-4': canPublishDashboards,
        })}
      >
        <S2Typography
          translatedContent={truncatedOrganizationName}
          variant="headline-7-alt"
          textColorVariant="gray-1000"
        />
        {canPublishDashboards ? (
          <IconArrowTopSvg svg={{ className: 'w-4 h-4 rotate-180' }} />
        ) : undefined}
      </div>
    );
  }, [truncatedOrganizationName, canPublishDashboards]);

  const generalAccessDropdownItems = useMemo(() => {
    return [
      {
        key: 'private',
        content: (
          <div className="flex items-center gap-1">
            <IconCheckMark24pxSvg
              svg={{ className: 'w-6 h-6 text-green-600' }}
            />
            {truncatedOrganizationName}
          </div>
        ),
        behavior: {
          type: 'onClick' as const,
          onClick: () => {},
        },
      },
      {
        key: 'public',
        content: (
          <div className="flex items-center gap-1">
            {isPublic ? (
              <IconCheckMark24pxSvg
                svg={{ className: 'w-6 h-6 text-green-600' }}
              />
            ) : (
              <div className="w-6 h-6"></div>
            )}
            Share Publicly
          </div>
        ),
        behavior: {
          type: 'onClick' as const,
          onClick: isPublic ? handleMakePrivate : handleMakePublic,
        },
      },
    ];
  }, [
    handleMakePrivate,
    handleMakePublic,
    isPublic,
    truncatedOrganizationName,
  ]);

  const [publicAccessDropdownOpen, setPublicAccessDropdownOpen] =
    useState(false);

  const publicAccessDropdownItems = useMemo(() => {
    return [
      {
        key: 'removeLink',
        content: language.removeLink(),
        behavior: {
          type: 'onClick' as const,
          onClick: handleMakePrivate,
        },
      },
    ];
  }, [handleMakePrivate, language]);

  const publicAccessTrigger = useMemo(() => {
    return (
      <div
        className={cn('flex gap-1 items-center', {
          '-mx-4': canPublishDashboards,
        })}
      >
        <S2Typography
          translatedContent={language.public()}
          variant="headline-7-alt"
          textColorVariant="gray-1000"
        />
        {canPublishDashboards ? (
          <IconArrowTopSvg svg={{ className: 'w-4 h-4 rotate-180' }} />
        ) : undefined}
      </div>
    );
  }, [language, canPublishDashboards]);

  return (
    <>
      <LightModal
        width="w-1/2 max-w-2xl"
        showModal={showModal && !showEmbedModal && !showDownloadModal}
        onClose={handleToggleShowModal}
        title={
          <S2Typography
            translatedContent={language.shareDashboard({
              dashboardName: dashboard.name,
            })}
            variant="headline-5"
            textColorVariant="gray-1000"
          />
        }
        actions={
          <div className="flex gap-2 items-center">
            {error && (
              <div className="flex items-center gap-1">
                <S2Typography
                  translatedContent={error}
                  variant="body-7"
                  textColorVariant="red-600"
                />
              </div>
            )}
            <PrimaryButtonLightBg
              icon={() =>
                isSaving ? (
                  <Spinner size={17} textColor="text-white" />
                ) : undefined
              }
              translatedContent={language.done()}
              onClick={handlePublish}
              disabled={false}
            />
          </div>
        }
      >
        <>
          <div className="flex flex-col" data-testid="publish-modal">
            <S2Typography
              translatedContent={language.generalAccess()}
              variant="headline-6-alt"
              textColorVariant="gray-1000"
            />
            <div className="flex justify-between mt-3">
              <div className="flex gap-2 items-center">
                <div className="w-8 h-8 bg-royal-200 rounded-full flex items-center justify-center">
                  <IconBuildingSvg />
                </div>
                <div className="flex flex-col">
                  <div
                    className="flex w-fit"
                    data-testid="general-access-dropdown"
                  >
                    {canPublishDashboards ? (
                      <Dropdown
                        trigger={generalAccessTrigger}
                        items={generalAccessDropdownItems}
                        isOpen={generalAccessDropdownOpen}
                        setIsOpen={setGeneralAccessDropdownOpen}
                      />
                    ) : (
                      generalAccessTrigger
                    )}
                  </div>
                  <S2Typography
                    translatedContent={language.anyoneInThisOrganizationCanEdit()}
                    variant="small-label"
                    textColorVariant="gray-600"
                  />
                </div>
              </div>
              <div className="py-2">
                <S2Typography
                  translatedContent={language.editor()}
                  variant="body-7-medium"
                  textColorVariant="gray-900"
                />
              </div>
            </div>

            {isPublic && (
              <div className="flex justify-between mt-3">
                <div className="flex gap-2 items-center">
                  <div className="w-8 h-8 bg-teal-200 rounded-full flex items-center justify-center">
                    <IconGlobeSvg />
                  </div>
                  <div className="flex flex-col">
                    <div className="flex w-fit">
                      {canPublishDashboards ? (
                        <Dropdown
                          trigger={publicAccessTrigger}
                          items={publicAccessDropdownItems}
                          isOpen={publicAccessDropdownOpen}
                          setIsOpen={setPublicAccessDropdownOpen}
                        />
                      ) : (
                        publicAccessTrigger
                      )}
                    </div>
                    <S2Typography
                      translatedContent={language.anyoneCanAccessThisDashboard()}
                      variant="small-label"
                      textColorVariant="gray-600"
                    />
                  </div>
                </div>
                <div className="py-2">
                  <S2Typography
                    translatedContent={language.viewer()}
                    variant="body-7-medium"
                    textColorVariant="gray-900"
                  />
                </div>
              </div>
            )}
          </div>
          <div
            className="flex absolute "
            style={{ top: 'calc(100% + 8px)', left: 0, right: 0 }}
          >
            <div className="w-full overflow-hidden rounded-2xl bg-white">
              <TouchTarget
                content={() => (
                  <div className="flex items-center justify-between px-6 py-5">
                    <S2Typography
                      translatedContent={language.embedDashboard()}
                      variant="headline-7-alt"
                      textColorVariant="gray-1000"
                    />
                    <IconChevronRightSvg
                      svg={{ className: 'h-6 w-6 text-gray-1000' }}
                    />
                  </div>
                )}
                behavior={{
                  type: 'onClick',
                  onClick: handleToggleEmbedModal,
                }}
              />
              {showDownloadDashboardButton && (
                <>
                  <div className="border-t border-gray-200" />
                  <TouchTarget
                    content={() => (
                      <div className="flex items-center justify-between px-6 py-5">
                        <S2Typography
                          translatedContent={language.downloadDashboard()}
                          variant="headline-7-alt"
                          textColorVariant="gray-1000"
                        />
                        <IconChevronRightSvg
                          svg={{ className: 'h-6 w-6 text-gray-1000' }}
                        />
                      </div>
                    )}
                    behavior={{
                      type: 'onClick',
                      onClick: handleToggleDownloadModal,
                    }}
                  />
                </>
              )}
            </div>
          </div>
        </>
      </LightModal>
      <LightModal
        width="w-1/2 max-w-2xl"
        showModal={showEmbedModal}
        onClose={handleToggleEmbedModal}
        title={
          <div className="flex items-center gap-1">
            <TouchTarget
              content={() => (
                <IconArrowLeft24pxSvg
                  svg={{ className: 'w-6 h-6 text-gray-600 ' }}
                />
              )}
              behavior={{ type: 'onClick', onClick: handleToggleEmbedModal }}
            />

            <S2Typography
              translatedContent={language.copyPublicEmbedCode()}
              variant="headline-5"
              textColorVariant="gray-1000"
            />
          </div>
        }
        actions={
          <PrimaryButtonLightBg
            translatedContent={language.copy()}
            icon={() => <ContentCopyIcon className="text-white w-3.5 h-3.5" />}
            onClick={handleCopyEmbedCode}
            disabled={false}
          />
        }
      >
        <div className="flex flex-col">
          <S2Typography
            translatedContent={language.embedDashboardInYourPage({
              dashboardName: dashboard.name,
            })}
            variant="body-6"
            textColorVariant="gray-900"
          />
          <div className="border-solid-1 border-gray-200 rounded-md bg-gray-200/30 p-2 mt-4">
            <pre className="overflow-x-auto">
              <code>{iframeCode.replaceAll(' ', '\n  ')}</code>
            </pre>
          </div>
          {!isPublic && (
            <div className="mt-4 flex gap-2 bg-[#F8ECAE] rounded-lg py-2 px-4 items-center">
              <div className="w-4 h-4">
                <IconInformationSvg
                  svg={{ width: 16, height: 16, className: 'text-[#725B07]' }}
                />
              </div>
              <div>
                <S2Typography
                  translatedContent={language.privateDashboardWarning({
                    organizationName: truncatedOrganizationName,
                  })}
                  variant="body-6"
                  textColorVariant="gray-900"
                />
              </div>
            </div>
          )}
        </div>
      </LightModal>
      <LightModal
        width="w-full max-w-md min-w-[443px]"
        showModal={showDownloadModal}
        onClose={handleToggleDownloadModal}
        title={
          <div className="flex items-center gap-4">
            <TouchTarget
              content={() => (
                <IconArrowLeft24pxSvg
                  svg={{ className: 'w-6 h-6 text-gray-600 ' }}
                />
              )}
              behavior={{ type: 'onClick', onClick: handleToggleDownloadModal }}
            />

            <S2Typography
              translatedContent={language.downloadDashboard()}
              variant="headline-4"
              textColorVariant="gray-1000"
              dangerouslyOverrideClassNameFn={(className) =>
                className
                  .replace('font-medium', 'font-normal')
                  .replace('leading-normal', 'leading-[1.3]')
              }
            />
          </div>
        }
        actions={
          <div className="flex w-full items-center justify-end pt-4">
            <PrimaryButtonLightBg
              translatedContent={language.downloadAnalysis()}
              onClick={handleDownloadDashboard}
              disabled={!onDownloadDashboard || downloadPending}
              innerClassName="h-9 min-w-[80px] gap-1 px-3 py-2"
              icon={() =>
                downloadPending ? (
                  <CircularProgress
                    size={16}
                    thickness={4}
                    color="inherit"
                    className="text-gray-400"
                  />
                ) : (
                  <IconDownloadSvg svg={{ className: 'h-4 w-4 text-white' }} />
                )
              }
            />
          </div>
        }
      >
        <div className="flex flex-col">
          <S2Typography
            translatedContent={language.downloadDashboardAsFile({
              dashboardName: dashboard.name,
            })}
            variant="body-6"
            textColorVariant="gray-1000"
            dangerouslyOverrideClassNameFn={(className) =>
              className
                .replace('text-sm', 'text-[16px]')
                .replace('leading-normal', 'leading-[1.5]')
                .concat(' tracking-[-0.16px]')
            }
          />
          <div className="mt-4 flex items-center gap-3">
            <div className="flex size-8 items-center justify-center rounded-[48px] bg-[#E7E8E8] p-2">
              <IconDocumentFileSvg
                svg={{ className: 'h-4 w-4 text-gray-900' }}
              />
            </div>
            <div className="flex flex-col">
              <S2Typography
                translatedContent={language.documentFile()}
                variant="headline-7-alt"
                textColorVariant="gray-1000"
              />
              <S2Typography
                translatedContent={language.downloadDashboardDocxDescription()}
                variant="small-label"
                textColorVariant="gray-600"
              />
            </div>
          </div>
        </div>
      </LightModal>
      {renderTrigger({ onClick: handleToggleShowModal })}
    </>
  );
};
